import React from 'react';
import {
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormGroup,
    InputLabel,
    Menu,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import { toast } from 'react-toastify';
import Group from '../../../../@types/group';
import {
    Component,
    ComponentLifecycleTransition,
    ComponentState,
    ReleaseLevel,
} from '../../../../@types/component';
import { ComponentEditorProps } from '../../../../@types/editor-props';
import { ResourceType } from '../../../../@types/resource-type';
import { ResourceAction } from '../../../../@types/resource-action';
import { getResourceNameWithAscendants } from '../../../../utils/resource-utils';
import { uuid } from '../../../../utils/utils';
import { useWorkspaceContext } from '../../../../context/workspaceContext';
import componentService from '../../../../services/component.service';
import ErrorAlertComponent from '../../../../components/ErrorAlert.component';

const STATE_COLOR: Record<ComponentState, 'default' | 'info' | 'success' | 'warning'> = {
    [ComponentState.DRAFT]: 'info',
    [ComponentState.PUBLISHED]: 'success',
    [ComponentState.DEPRECATED]: 'warning',
    [ComponentState.ARCHIVED]: 'default',
};

/**
 * Form editor for a Component's core fields -- the Component analogue of the Event Sequence editor.
 *
 * In create mode (no {@code component.id}) the working area is selectable. The unique identifier is
 * always caller-supplied and mandatory. Once the Component exists, the unique identifier and
 * name/description stay editable while it is a DRAFT; the working area is then immutable.
 */
const ComponentEditor: React.FC<ComponentEditorProps> = (props: ComponentEditorProps) => {
    const { component, isActive, updateTab } = props;
    const {
        workspaceObjects, setWorkspaceObjects, setCurrentWorkspaceObject,
        currentAction, setCurrentAction, getAdditionalResource, publishResourceUpdate,
    } = useWorkspaceContext();

    const isCreate: boolean = !component?.id;

    const [uniqueId, setUniqueId] = React.useState<string>(component?.uniqueId ?? '');
    const [name, setName] = React.useState<string>(component?.name ?? '');
    const [description, setDescription] = React.useState<string>(component?.description ?? '');
    const [groupId, setGroupId] = React.useState<string | null>(
        component?.groupId ? String(component.groupId) : null,
    );
    const [saving, setSaving] = React.useState<boolean>(false);
    const [error, setError] = React.useState<string | null>(null);
    const [pendingTransition, setPendingTransition] =
        React.useState<ComponentLifecycleTransition | null>(null);
    const [releaseLevel, setReleaseLevel] = React.useState<ReleaseLevel | null>(null);
    const [releaseMenuAnchor, setReleaseMenuAnchor] = React.useState<HTMLElement | null>(null);
    const [releaseNotes, setReleaseNotes] = React.useState<string>('');

    const uuidRef = React.useRef(uuid());
    const componentIdRef = React.useRef(component?.id);

    const groups: Group[] = ((getAdditionalResource(ResourceType.GROUPS).resources as Group[]) ?? [])
        .slice()
        .sort((a, b) => a.name.localeCompare(b.name));
    const editableGroups: Group[] = groups.filter((grp) => grp.canAddOrEditResources());

    const ownerGroup: Group | undefined = component?.groupId
        ? groups.find((grp) => grp.id === component.groupId)
        : undefined;
    const groupLabel: string = ownerGroup
        ? getResourceNameWithAscendants(ownerGroup, ResourceType.GROUPS)
        : (component?.groupId ? `#${component.groupId}` : '');

    const isEditable: boolean = isCreate || component?.state === ComponentState.DRAFT;
    const nameValid: boolean = name.trim() !== '';
    const uniqueIdValid: boolean = uniqueId.trim() !== '';

    const applySaved = (saved: Component): void => {
        const workspaceObject = { objectType: ResourceType.COMPONENT, object: saved };
        setCurrentWorkspaceObject(workspaceObject);
        if (workspaceObjects?.objectType === ResourceType.COMPONENT) {
            setWorkspaceObjects({ objectType: ResourceType.COMPONENT });
        }
        publishResourceUpdate({
            type: ResourceType.COMPONENT,
            id: saved.id,
            updatedData: saved,
            source: 'save',
        });
        updateTab(workspaceObject);
    };

    /**
     * What each transition does and cannot undo. Shown before it is applied.
     */
    const TRANSITION_WARNING: Record<ComponentLifecycleTransition, string> = {
        [ComponentLifecycleTransition.PUBLISH]:
            'Publishing creates a new, immutable version from this draft, readable by every group '
            + 'and available for other models to pin. The draft itself is unaffected and stays open '
            + 'for further editing -- none of its mapping or test runs carry over to the published '
            + 'copy. This cannot be undone.',
        [ComponentLifecycleTransition.DEPRECATE]:
            'Deprecating discourages new use of this version. Models that already pin it keep '
            + 'working. This cannot be undone.',
        [ComponentLifecycleTransition.ARCHIVE]:
            'Archiving retires this version from the library. Models that already pin it keep '
            + 'working, and it stays readable for them. This cannot be undone.',
    };

    const availableTransitions: ComponentLifecycleTransition[] = React.useMemo(() => {
        switch (component?.state) {
            case ComponentState.DRAFT:
                return [ComponentLifecycleTransition.PUBLISH];
            case ComponentState.PUBLISHED:
                return [ComponentLifecycleTransition.DEPRECATE, ComponentLifecycleTransition.ARCHIVE];
            case ComponentState.DEPRECATED:
                return [ComponentLifecycleTransition.ARCHIVE];
            default:
                return [];
        }
    }, [component?.state]);

    const closePendingTransition = (): void => {
        setPendingTransition(null);
        setReleaseNotes('');
    };

    const applyTransition = (transition: ComponentLifecycleTransition): void => {
        if (!component?.id) {
            return;
        }
        setSaving(true);
        setError(null);
        componentService.applyLifecycleTransition(component.id, transition, releaseLevel ?? undefined,
            transition === ComponentLifecycleTransition.PUBLISH ? releaseNotes.trim() : undefined)
            .then(saved => {
                if (transition === ComponentLifecycleTransition.PUBLISH) {
                    // Publishing snapshots this draft into a separate, new Component row -- this
                    // tab keeps showing the still-editable draft; only the library list needs to
                    // learn a new published version now exists.
                    publishResourceUpdate({
                        type: ResourceType.COMPONENT,
                        id: saved.id,
                        updatedData: saved,
                        source: 'save',
                    });
                    toast.success(`Published v${saved.version} of ${saved.name}`);
                } else {
                    applySaved(saved);
                    toast.success(`Component ${transition.toLowerCase()}d`);
                }
            })
            .catch((err: Error) => setError(err.message))
            .finally(() => {
                setSaving(false);
                setReleaseLevel(null);
                closePendingTransition();
            });
    };

    const handleSave = (): void => {
        if (!isEditable || !nameValid || !uniqueIdValid || saving) {
            return;
        }
        setError(null);
        setSaving(true);

        const request: Promise<Component> = isCreate
            ? componentService.createComponent(groupId ?? editableGroups[0]?.id ?? '', {
                uniqueId: uniqueId.trim(),
                name: name.trim(),
                description,
            })
            : componentService.updateComponent(component!.id!, {
                uniqueId: uniqueId.trim(),
                name: name.trim(),
                description,
            });

        request
            .then((saved: Component) => {
                applySaved(saved);
                toast.success(isCreate ? 'Component created successfully' : 'Component updated successfully');
            })
            .catch((err: Error) => {
                setError(err.message);
                toast.error(isCreate ? 'Error creating Component' : 'Error updating Component');
            })
            .finally(() => setSaving(false));
    };

    React.useEffect(() => {
        if (componentIdRef.current !== component?.id) {
            componentIdRef.current = component?.id;
            setUniqueId(component?.uniqueId ?? '');
            setName(component?.name ?? '');
            setDescription(component?.description ?? '');
            setGroupId(component?.groupId ? String(component.groupId) : null);
            setError(null);
        }
        if (isActive && currentAction === ResourceAction.SAVE) {
            setCurrentAction(null);
            handleSave();
        }
        // Properties is what opens this editor, so there is nothing to select: clear it so it does
        // not linger for whatever is opened next.
        if (isActive && currentAction === ResourceAction.PROPERTIES) {
            setCurrentAction(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [component, currentAction, isActive]);

    return (
        <FormGroup className="formStyle">
            <Stack className="formControl" spacing={2}>
                {!isCreate && (
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Chip
                            size="small"
                            label={component?.state ?? 'UNKNOWN'}
                            color={component ? (STATE_COLOR[component.state] ?? 'default') : 'default'}
                        />
                        {component?.version
                            ? <Chip size="small" variant="outlined" label={`v${component.version}`} />
                            : (
                                <Typography variant="caption" color="text.secondary">
                                    (a draft carries no version of its own -- publishing creates a
                                    versioned copy)
                                </Typography>
                            )}
                        {!isEditable && (
                            <Typography variant="caption" color="text.secondary">
                                only a draft can be edited
                            </Typography>
                        )}
                        <Box sx={{ flexGrow: 1 }} />
                        {availableTransitions.map(transition => (
                            <Button
                                key={transition}
                                size="small"
                                variant={transition === ComponentLifecycleTransition.PUBLISH
                                    ? 'contained' : 'outlined'}
                                disabled={saving}
                                onClick={(e): void => {
                                    if (transition === ComponentLifecycleTransition.PUBLISH) {
                                        setReleaseMenuAnchor(e.currentTarget);
                                    } else {
                                        setPendingTransition(transition);
                                    }
                                }}
                            >
                                {transition.charAt(0) + transition.slice(1).toLowerCase()}
                            </Button>
                        ))}
                        {/* Publishing has no safe default release level, so clicking Publish opens
                            this menu instead of confirming directly -- the same pattern the Create
                            button uses when a dataset can create more than one kind of thing. */}
                        <Menu
                            anchorEl={releaseMenuAnchor}
                            open={releaseMenuAnchor != null}
                            onClose={(): void => setReleaseMenuAnchor(null)}
                        >
                            {Object.values(ReleaseLevel).map(level => (
                                <MenuItem
                                    key={level}
                                    onClick={(): void => {
                                        setReleaseMenuAnchor(null);
                                        setReleaseLevel(level);
                                        setPendingTransition(ComponentLifecycleTransition.PUBLISH);
                                    }}
                                >
                                    {level.charAt(0) + level.slice(1).toLowerCase()}
                                </MenuItem>
                            ))}
                        </Menu>
                    </Stack>
                )}

                <Dialog open={pendingTransition != null} onClose={closePendingTransition}>
                    <DialogTitle>
                        {pendingTransition
                            ? pendingTransition === ComponentLifecycleTransition.PUBLISH
                                ? `Publish ${component?.name ?? 'this component'} as a ${releaseLevel} release?`
                                : `${pendingTransition.charAt(0)}${pendingTransition.slice(1).toLowerCase()}`
                                    + ` ${component?.name ?? 'this component'}?`
                            : ''}
                    </DialogTitle>
                    <DialogContent>
                        <Typography variant="body2">
                            {pendingTransition ? TRANSITION_WARNING[pendingTransition] : ''}
                        </Typography>
                        {pendingTransition === ComponentLifecycleTransition.PUBLISH && (
                            <TextField
                                label="Release notes"
                                aria-label="release notes"
                                variant="outlined"
                                size="small"
                                fullWidth
                                sx={{ mt: 2 }}
                                value={releaseNotes}
                                onChange={(e): void => setReleaseNotes(e.target.value)}
                                multiline
                                minRows={3}
                                required
                                error={releaseNotes.trim() === ''}
                                helperText="What changed since the previous release -- required to publish"
                            />
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button size="small" onClick={closePendingTransition}>Cancel</Button>
                        <Button
                            size="small"
                            variant="contained"
                            disabled={saving
                                || (pendingTransition === ComponentLifecycleTransition.PUBLISH
                                    && releaseNotes.trim() === '')}
                            onClick={(): void => {
                                if (pendingTransition) {
                                    applyTransition(pendingTransition);
                                }
                            }}
                        >
                            {pendingTransition
                                ? pendingTransition.charAt(0) + pendingTransition.slice(1).toLowerCase()
                                : ''}
                        </Button>
                    </DialogActions>
                </Dialog>

                <TextField
                    label="Unique ID"
                    aria-label="unique id"
                    variant="outlined"
                    size="small"
                    id={`component-unique-id-${uuidRef.current}`}
                    value={uniqueId}
                    onChange={(e): void => setUniqueId(e.target.value)}
                    disabled={!isEditable}
                    required
                    error={isEditable && !uniqueIdValid}
                    helperText={isCreate
                        ? 'Unique identifier for this Component'
                        : 'Unique identifier for this Component -- editable only while DRAFT'}
                />

                <TextField
                    label="Name"
                    aria-label="name"
                    variant="outlined"
                    size="small"
                    id={`component-name-${uuidRef.current}`}
                    value={name}
                    onChange={(e): void => setName(e.target.value)}
                    disabled={!isEditable}
                    required
                />

                <TextField
                    label="Description"
                    aria-label="description"
                    variant="outlined"
                    size="small"
                    id={`component-description-${uuidRef.current}`}
                    value={description}
                    onChange={(e): void => setDescription(e.target.value)}
                    disabled={!isEditable}
                    multiline
                    minRows={3}
                />

                {/* Only a published release ever carries release notes -- a DRAFT has none of its
                    own (they are captured once, at the moment it is published) -- so this is
                    read-only and only shown once there is something to show. */}
                {component?.releaseNotes && (
                    <TextField
                        label="Release Notes"
                        aria-label="release notes"
                        variant="outlined"
                        size="small"
                        id={`component-release-notes-${uuidRef.current}`}
                        value={component.releaseNotes}
                        disabled
                        multiline
                        minRows={3}
                        maxRows={8}
                        helperText="What changed in this release"
                    />
                )}

                {isCreate ? (
                    <FormControl size="small" disabled={editableGroups.length === 0}>
                        <InputLabel id={`component-group-label-${uuidRef.current}`}>Working Area</InputLabel>
                        <Select
                            labelId={`component-group-label-${uuidRef.current}`}
                            id={`component-group-${uuidRef.current}`}
                            label="Working Area"
                            value={groupId ?? String(editableGroups[0]?.id ?? '')}
                            onChange={(e): void => setGroupId(e.target.value as string)}
                        >
                            {editableGroups.map((grp) => (
                                <MenuItem key={grp.id} value={String(grp.id)}>
                                    {getResourceNameWithAscendants(grp, ResourceType.GROUPS)}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                ) : (
                    <FormControl size="small" disabled>
                        <InputLabel id={`component-group-label-${uuidRef.current}`}>Working Area</InputLabel>
                        <Select
                            labelId={`component-group-label-${uuidRef.current}`}
                            id={`component-group-${uuidRef.current}`}
                            label="Working Area"
                            value={groupLabel}
                            renderValue={(value): string => value as string}
                        >
                            <MenuItem value={groupLabel}>{groupLabel}</MenuItem>
                        </Select>
                    </FormControl>
                )}

                <div>
                    <Button
                        className="formBtn"
                        id={`save-component-${uuidRef.current}`}
                        size="small"
                        variant="contained"
                        startIcon={<SaveAltIcon />}
                        onClick={handleSave}
                        disabled={!isEditable || saving || !nameValid || !uniqueIdValid
                            || (isCreate && editableGroups.length === 0)}
                    >
                        Save
                    </Button>
                </div>

                {error && <ErrorAlertComponent message={error} />}
            </Stack>
        </FormGroup>
    );
};

export default ComponentEditor;
